// Bring your own probabilities.
//
// Someone with their own model — a spreadsheet, a notebook, a tipping comp's
// ratings — hands the app a CSV and every strategy, backtest and calibration
// report treats their numbers exactly like a built-in's. The engine devigs,
// screens, combines and stakes them. It never CHANGES them: the only work done
// here is the join, turning a date and two team names into a matchKey.
//
// ROWS THAT DID NOT JOIN ARE KEPT. An upload that silently matched three of
// forty rows is worse than one that says so, so every refusal comes back with
// the row and the reason and the screen prints the first few. That is also why
// a bare `62` in the probability column is refused rather than read as 62% —
// see `readP` in engine/models/userUpload.js.
//
// LEAF MODULE. It imports storage, demo and the engine, and nothing else in
// `lib/stores/` or `store.js`, so `store.js` can import it without a cycle.

import { computed, ref } from 'vue';
import { apiBase } from '../api-base.js';
import { records, prefs } from '../../core/storage.js';
import { isDemo } from '../../core/demo.js';
import { parseCsv, rowsToObjects } from '../engine/csv.js';
import { fnv1a } from '../engine/hash.js';
import { normaliseProbSet } from '../engine/models/userUpload.js';
import { SPORTS } from '../engine/teams.js';

/** The `records` entity a saved probability set is stored as. */
export const PROB_SET_ENTITY = 'prob-set';

/** Where the selected set's id lives in `prefs`. */
export const PROB_SET_PREF = 'pref.probSet';

/** The two header formats the upload accepts, for the small print and the tests. */
export const HEADER_FORMATS = [
  ['matchKey', 'selection', 'p'],
  ['sport', 'date', 'home', 'away', 'selection', 'p'],
];

/** How many refused rows a screen shows before it stops listing them. */
export const UNMATCHED_SHOWN = 5;

/** The sets on this device, newest first. */
export const probSets = ref([]);

/** Which one the pad is running on, or null. */
export const selectedProbSetId = ref(null);

export const selectedProbSet = computed(() =>
  probSets.value.find((s) => s.id === selectedProbSetId.value) ?? null);

const newestFirst = (a, b) => String(b.createdAt).localeCompare(String(a.createdAt));

/** A short id for a set made on this device. */
const newId = (seed) => `ps-${fnv1a(`${seed}${Math.random()}`).toString(36).padStart(6, '0').slice(-6)}`;

// --- reading a CSV -------------------------------------------------------------

// Headers are matched on letters and digits alone, so "Match Key", "match_key"
// and "matchKey" are the same column and a stray space is not a failed upload.
const headerKey = (h) => String(h ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const COLUMNS = {
  matchkey: 'matchKey',
  selection: 'selection',
  p: 'p',
  sport: 'sport',
  date: 'date',
  home: 'home',
  away: 'away',
};

/** Which columns are missing, in the words the two accepted formats use. */
function headerProblem(names) {
  const has = (n) => names.includes(n);
  if (!has('selection') || !has('p')) return 'the header needs a selection column and a p column';
  if (has('matchkey')) return null;
  if (has('date') && has('home') && has('away')) return null;
  return 'the header needs either matchKey, or date plus home and away';
}

/**
 * A CSV, as a probability set.
 *
 * Pure: the id and the timestamp can be passed in, so a test gets the same
 * object twice. Rows carrying their own `sport` column are grouped and joined
 * in that sport's own calendar zone; everything else uses the sport chosen on
 * the upload screen.
 */
export function probSetFromCsv(text, {
  sport = '',
  name = '',
  id = null,
  createdAt = null,
} = {}) {
  const parsed = parseCsv(text);
  const names = parsed.header.map(headerKey);
  const at = createdAt ?? new Date().toISOString();
  const set = {
    id: id ?? newId(`${name}${sport}${at}`),
    entity: PROB_SET_ENTITY,
    name: String(name ?? '').trim() || 'Untitled set',
    sport,
    rows: {},
    total: 0,
    matched: 0,
    unmatched: [],
    createdAt: at,
    source: 'csv',
    error: null,
  };

  const problem = headerProblem(names);
  if (problem) return { ...set, error: problem };

  const objects = rowsToObjects({ header: names.map((n) => COLUMNS[n] ?? n), rows: parsed.rows });
  // Every blank line in the middle of a file would otherwise be a refused row
  // with a reason nobody needs to read.
  const rows = objects.filter((r) => Object.values(r).some((v) => String(v).trim() !== ''));
  set.total = rows.length;

  // One pass per sport, because the date column is read in the COMPETITION's
  // zone and a mixed file would otherwise join the EPL rows on Sydney's clock.
  const groups = new Map();
  for (const row of rows) {
    const key = String(row.sport || sport || '').trim().toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }

  for (const [rowSport, group] of groups) {
    const out = normaliseProbSet(group, { sport: rowSport, knownTeamsOnly: true });
    for (const [matchKey, sides] of Object.entries(out.rows)) {
      set.rows[matchKey] = { ...set.rows[matchKey], ...sides };
    }
    set.matched += out.matched;
    set.unmatched.push(...out.unmatched);
  }

  return set;
}

// --- what is saved -------------------------------------------------------------

/** Just the fields worth storing. The refused rows are a REPORT, not data. */
function toRow(set) {
  return {
    id: set.id,
    entity: PROB_SET_ENTITY,
    name: set.name,
    sport: set.sport,
    rows: set.rows ?? {},
    matched: set.matched ?? 0,
    unmatched: (set.unmatched ?? []).slice(0, UNMATCHED_SHOWN),
    createdAt: set.createdAt ?? new Date().toISOString(),
    source: set.source ?? 'csv',
  };
}

/** Seed the list from rows already read out of IndexedDB — no second read. */
export function seedProbSets(rows) {
  probSets.value = (rows ?? []).filter((r) => r?.entity === PROB_SET_ENTITY).sort(newestFirst);
  const stored = isDemo.value ? null : prefs[PROB_SET_PREF];
  selectedProbSetId.value = probSets.value.some((s) => s.id === stored) ? stored : null;
  return probSets.value;
}

/** Read the saved sets. Safe to call more than once; in /demo they are in memory. */
export async function loadProbSets() {
  if (isDemo.value) return probSets.value;
  try {
    return seedProbSets(await records.all());
  } catch {
    // Private mode, or no IndexedDB at all.
    return probSets.value;
  }
}

/** Save one. In /demo it is kept in memory and nothing is written. */
export async function saveProbSet(set) {
  const row = toRow(set);
  probSets.value = [row, ...probSets.value.filter((s) => s.id !== row.id)].sort(newestFirst);
  if (!isDemo.value) await records.put({ ...row });
  return row;
}

/** Delete one, and stop running on it if the pad was. */
export async function deleteProbSet(id) {
  const set = probSets.value.find((s) => s.id === id) ?? null;
  if (!set) return null;
  probSets.value = probSets.value.filter((s) => s.id !== id);
  if (selectedProbSetId.value === id) selectProbSet(null);
  if (!isDemo.value) await records.delete(id);
  return set;
}

/** Run the pad on this set. `null` goes back to no numbers at all. */
export function selectProbSet(id) {
  const next = probSets.value.some((s) => s.id === id) ? id : null;
  selectedProbSetId.value = next;
  if (isDemo.value) return next;
  if (next) prefs[PROB_SET_PREF] = next;
  else delete prefs[PROB_SET_PREF];
  return next;
}

// --- the server's copy ----------------------------------------------------------

/** The signed-in punter's Firebase token, or null. Imported late so a build without auth still runs. */
async function idToken() {
  try {
    const mod = await import('../../../addons/auth/index.js');
    return (await mod.getIdToken?.()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Pull down the sets posted to `/api/probabilities` from somebody's own
 * pipeline and merge them into the list.
 *
 * A modeller who generates probabilities nightly should not have to export a
 * CSV and tap Upload; they POST, and the phone finds them here. Local sets are
 * never overwritten by a server one of the same id — the device is where the
 * upload happened. Signed out, in /demo, or with no server: nothing happens,
 * quietly, because this runs on a boot nobody asked for.
 */
export async function syncProbSets() {
  if (isDemo.value) return probSets.value;
  const token = await idToken();
  if (!token) return probSets.value;

  let body;
  try {
    const res = await fetch(`${apiBase()}/api/probabilities`, {
      headers: { accept: 'application/json', authorization: `Bearer ${token}` },
    });
    if (!res.ok) return probSets.value;
    body = await res.json();
  } catch {
    return probSets.value;
  }

  const mine = new Set(probSets.value.map((s) => s.id));
  const fetched = [];
  for (const summary of body?.sets ?? []) {
    if (!summary?.id || mine.has(summary.id)) continue;
    try {
      const res = await fetch(`${apiBase()}/api/probabilities/${encodeURIComponent(summary.id)}`, {
        headers: { accept: 'application/json', authorization: `Bearer ${token}` },
      });
      if (!res.ok) continue;
      const set = await res.json();
      fetched.push(toRow({ ...set, source: 'api' }));
    } catch { /* one set that will not come down is not a failed sync */ }
  }
  if (!fetched.length) return probSets.value;

  probSets.value = [...fetched, ...probSets.value].sort(newestFirst);
  await Promise.all(fetched.map((row) => records.put({ ...row }).catch(() => undefined)));
  return probSets.value;
}

/** Every sport a set can be uploaded for, in the order the editor lists them. */
export const PROB_SET_SPORTS = Object.keys(SPORTS);
